import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "../../utils/hooks/useAxios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { handleFormErrors } from "../../utils/helpers";
import { useSystem } from "@/utils/hooks/useLocation";
import { SYSTEMS } from "@/utils/constants";

// ************* Mutations *************
export const useCreateGroupMutation = (setError) => {
  const { t } = useTranslation();
  const { axiosProtected } = useAxios();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload) => axiosProtected.post("groups", payload),
    mutationKey: "createGroup",
    onSuccess() {
      navigate("/app/user_management/groups");
      toast.success(t("GROUP_CREATED_SUCCESSFULLY"));
    },
    onError(err) {
      handleFormErrors(err, setError, t, "GROUP_CREATED_FAILED");
    },
  });
};
export const useUpdateGroupMutation = (setError) => {
  const { t } = useTranslation();
  const { axiosProtected } = useAxios();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ id, ...payload }) =>
      axiosProtected.put(`groups/${id}`, payload),
    mutationKey: "updateGroup",
    onSuccess() {
      navigate("/app/user_management/groups");
      toast.success(t("GROUP_UPDATED_SUCCESSFULLY"));
    },
    onError(err) {
      handleFormErrors(err, setError, t, "GROUP_UPDATED_FAILED");
    },
  });
};
export const useDeleteGroupMutation = () => {
  const { t } = useTranslation();
  const { axiosProtected } = useAxios();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ id }) => axiosProtected.delete(`groups/${id}`),
    mutationKey: "deleteGroup",
    onSuccess() {
      navigate("/app/user_management/groups");

      toast.success(t("GROUP_DELETED_SUCCESSFULLY"));
    },
    onError(err) {
      // Handle other types of errors
      return toast.error(err?.message || t("GROUP_DELETED_FAILED"));
    },
  });
};

// ************* Queries *************
export const useGroupsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["groups", params],
    queryFn: () => axiosProtected.get("groups", { params }),
  });
};
export const useSelectedGroupPermissionsQuery = (groupId) => {
  const { axiosProtected } = useAxios();
  return useQuery({
    queryKey: ["selected-group-permissions", groupId],
    queryFn: () => axiosProtected.get(`groups/${groupId}/category-permissions`),
    enabled: !!groupId,
  });
};
export const useGetOneGroupQuery = (groupId) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();

  return useQuery({
    queryKey: ["one-group", groupId],
    queryFn: () => axiosProtected.get(`groups/${groupId}/with-permissions`),
    enabled: !!groupId,
    onError(err) {
      return toast.error(err?.message || t("SOMETHING_WENT_WRONG"));
    },
  });
};

export const useCreateManualGroupMutation = (setFormError, onErrorCallback) => {
  const { axiosProtected } = useAxios();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();
  const system =
    currentSystem === SYSTEMS.makkah ? "makkah" : "madina";

  return useMutation({
    mutationFn: async (formData) => {
      return axiosProtected.post(`${system}/makkah-groups`, formData);
    },
    onSuccess: () => {
      toast.success(t("GROUP_CREATED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["makkah-groups"] });
      navigate(`/app/${currentSystem}/groups-upcoming`);
    },
    onError: (err) => {
      console.error("Manual group creation error:", err);
      if (setFormError) {
        handleFormErrors(err, setFormError, t, "GROUP_CREATION_FAILED");
      }
      if (onErrorCallback) {
        onErrorCallback(err);
      }
      toast.error(err?.response?.data?.message || t("GROUP_CREATION_FAILED"));
    },
  });
};

export const useGetOneMakkahGroupQuery = () => {
  const { axiosProtected } = useAxios();
  const { id } = useParams();
  const { currentSystem } = useSystem();
  const system =
    currentSystem === SYSTEMS.makkah ? "makkah" : "madina";

  return useQuery({
    queryKey: ["one-makkah-group", id, system],
    queryFn: () => axiosProtected.get(`${system}/makkah-groups/${id}`),
    enabled: !!id,
  });
};
export const useGetOneMakkahGroupSegmentQuery = (id) => {
  const { axiosProtected } = useAxios();

  return useQuery({
    queryKey: ["one-makkah-group-segment", id],
    queryFn: () => axiosProtected.get(`makkah/arrival-group-details/${id}`),
    enabled: !!id,
  });
};

export const useUpdateMakkahGroupMutation = (setFormError, onErrorCallback) => {
  const { axiosProtected } = useAxios();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();
  const system =
    currentSystem === SYSTEMS.makkah ? "makkah" : "madina";

  return useMutation({
    mutationFn: async ({ id, supervisor_mobile_number, ...formData }) => {
      return axiosProtected.post(`${system}/makkah-groups/update/${id}`, {
        ...formData,
        supervisor_mobile_number: `${supervisor_mobile_number.replace(
          "0",
          ""
        )}`,
      });
    },
    onSuccess: (data, variables) => {
      toast.success(t("GROUP_UPDATED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["makkah-groups"] });
      queryClient.invalidateQueries({
        queryKey: ["one-makkah-group", variables.id],
      });
      navigate(`/app/${currentSystem}/groups-upcoming`);
    },
    onError: (err) => {
      console.error("Group update error:", err);
      if (setFormError) {
        handleFormErrors(err, setFormError, t, "GROUP_UPDATE_FAILED");
      }
      if (onErrorCallback) {
        onErrorCallback(err);
      }
      toast.error(err?.response?.data?.message || t("GROUP_UPDATE_FAILED"));
    },
  });
};

// ************* Arrival Group Details Mutations *************
export const useCreateArrivalGroupDetailMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { id: groupId } = useParams();

  return useMutation({
    mutationFn: async (formData) => {
      return axiosProtected.post("makkah/arrival-group-details", {
        ...formData,
        makkah_group_id: groupId,
      });
    },
    onSuccess: (data, variables) => {
      toast.success(
        t("GROUP_DETAIL_CREATED_SUCCESSFULLY") || t("SAVED_SUCCESSFULLY")
      );
      queryClient.invalidateQueries({ queryKey: ["makkah-groups"] });
      queryClient.invalidateQueries({
        queryKey: ["one-makkah-group", groupId],
      });
    },
    onError: (err) => {
      console.error("Arrival group detail creation error:", err);
      toast.error(
        err?.response?.data?.message ||
        t("GROUP_DETAIL_CREATION_FAILED") ||
        t("SOMETHING_WENT_WRONG")
      );
    },
  });
};

export const useUpdateArrivalGroupDetailMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { id: groupId } = useParams();

  return useMutation({
    mutationFn: async ({ id, ...formData }) => {
      return axiosProtected.post(
        `makkah/arrival-group-details/update/${id}`,
        {
          ...formData,
          makkah_group_id: groupId,
        }
      );
    },
    onSuccess: () => {
      toast.success(
        t("GROUP_DETAIL_UPDATED_SUCCESSFULLY") || t("SAVED_SUCCESSFULLY")
      );
      queryClient.invalidateQueries({ queryKey: ["makkah-groups"] });
      queryClient.invalidateQueries({
        queryKey: ["one-makkah-group", groupId],
      });
    }
  });
};

export const useDeleteArrivalGroupDetailMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { id: groupId } = useParams();

  return useMutation({
    mutationFn: async ({ id }) => {
      return axiosProtected.delete(`makkah/arrival-group-details/delete/${id}`);
    },
    onSuccess: (data, variables) => {
      toast.success(
        t("GROUP_DETAIL_DELETED_SUCCESSFULLY") || t("DELETED_SUCCESSFULLY")
      );
      queryClient.invalidateQueries({ queryKey: ["makkah-groups"] });
      queryClient.invalidateQueries({
        queryKey: ["one-makkah-group", groupId],
      });
    },
    onError: (err) => {
      console.error("Arrival group detail delete error:", err);
      toast.error(
        err?.response?.data?.message ||
        t("GROUP_DETAIL_DELETE_FAILED") ||
        t("SOMETHING_WENT_WRONG")
      );
    },
  });
};

